import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Button, CircularProgress, Paper, TextField, Typography,
} from '@mui/material';
import { SupplierPaymentService } from '../../services/supplierPayment.service';
import { SupplierBillService } from '../../services/supplierBill.service';
import { SupplierService } from '../../services/supplier.service';
import { loadSuppliersSafely } from '../../utils/supplierAccess';
import { AccountService } from '../../services/account.service';
import { AuthService } from '../../services/auth.service';
import { SupplierBill } from '../../interfaces/procurement/supplierBill.interface';
import { Supplier } from '../../interfaces/procurement/supplier.interface';
import { Account } from '../../interfaces/accounting.interface';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { useToast } from '../../context/ToastContext';
import SearchableSelect from '../common/SearchableSelect';
import { depositAccountOptionsFor, foreignCurrencyNameHint } from '../../utils/postingAccounts';
import { ExchangeRateService } from '../../services/exchangeRate.service';
import { isForeignCurrency, formatFc } from '../../utils/fcDocuments';
import { formatMoney } from '../../utils/format';
import { toLocalIsoDate } from '../../utils/dateUtils';
import { PaymentWithholdingSection, PaymentWithholdingValue } from './withholding/PaymentWithholdingSection';

const today = () => toLocalIsoDate();

export const SupplierPaymentCreate: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const lockedBillId = searchParams.get('supplierBillId');

  const institutionId = AuthService.getSelectedInstitution()!;

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [selectedSupplierId, setSelectedSupplierId] = useState('');
  const [bills, setBills] = useState<SupplierBill[]>([]);
  const [selectedBill, setSelectedBill] = useState<SupplierBill | null>(null);
  const [allAssetAccounts, setAllAssetAccounts] = useState<Account[]>([]);

  const [paymentDate, setPaymentDate] = useState(today());
  const [amount, setAmount] = useState<number | ''>('');
  const [paymentAccountId, setPaymentAccountId] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [reference, setReference] = useState('');

  const [withholding, setWithholding] = useState<PaymentWithholdingValue>({ lines: [], total: 0, pending: false });

  const [loadingBill, setLoadingBill] = useState(!!lockedBillId);
  const [loadingSuppliers, setLoadingSuppliers] = useState(!lockedBillId);
  const [saving, setSaving] = useState(false);

  // ---- B4-C: foreign-currency bill: the payment states the FC amount and the rate of the day ----
  const currency = selectedBill?.currencyCode ?? 'BDT';
  const foreign = isForeignCurrency(currency);
  const [rate, setRate] = useState<number | ''>('');
  const [rateHint, setRateHint] = useState('');
  const rateTouched = useRef(false);
  const assetAccounts = depositAccountOptionsFor(allAssetAccounts, currency);
  useEffect(() => {
    if (!foreign || rateTouched.current) return;
    let live = true;
    ExchangeRateService.latest(institutionId, currency, paymentDate)
      .then(r => { if (live) { setRate(r.rate); setRateHint(`Rate of ${r.rateDate} from the exchange-rate table`); } })
      .catch(() => { if (live) { setRate(''); setRateHint(`No ${currency} rate on file for ${paymentDate}: enter the rate`); } });
    return () => { live = false; };
  }, [foreign, currency, paymentDate]); // eslint-disable-line react-hooks/exhaustive-deps
  /** "USD 2,000.00 (BDT 243,700.00)" for a foreign-currency bill, the plain BDT total otherwise. */
  const billTotalLabel = (b: SupplierBill) => isForeignCurrency(b.currencyCode)
    ? `${formatFc(b.currencyCode!, b.foreignTotal)} (BDT ${b.totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`
    : String(b.totalAmount);

  const bdt = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const bdtOutstanding = (b: SupplierBill) => b.totalAmount - b.paidAmount - (b.debitedAmount ?? 0);
  /** Polish 4: "Paid"/"Outstanding" read "USD 500.00 (BDT 60,925.00)" on an FC bill and "BDT 500.00" otherwise. */
  const amountLabel = (b: SupplierBill, bdtAmount: number, fcAmount?: number) => isForeignCurrency(b.currencyCode)
    ? `${formatFc(b.currencyCode!, fcAmount)} (BDT ${bdt(bdtAmount)})`
    : `BDT ${bdt(bdtAmount)}`;
  /** Only a bill that still has a balance can take a payment (paid, reversed and draft bills are not offered). */
  const isPayable = (b: SupplierBill) => !['PAID', 'REVERSED', 'DRAFT'].includes(b.status) && bdtOutstanding(b) > 0.005;

  const openingAmount = (b: SupplierBill) => isForeignCurrency(b.currencyCode)
    ? (b.foreignOutstanding ?? 0)
    : b.totalAmount - b.paidAmount - (b.debitedAmount ?? 0);
  // ---- end B4-C ----

  // Outstanding amount
  const outstanding = selectedBill
    ? selectedBill.totalAmount - selectedBill.paidAmount - (selectedBill.debitedAmount ?? 0)
    : 0;

  // Load asset accounts
  useEffect(() => {
    AccountService.getAccountsByType('ASSET')
      .then((accounts: Account[]) => setAllAssetAccounts(accounts))
      .catch((e: unknown) => toast.showApiError(e, 'Failed to load accounts'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load locked bill or suppliers
  useEffect(() => {
    if (lockedBillId) {
      SupplierBillService.getById(lockedBillId)
        .then(bill => {
          setSelectedBill(bill);
          setAmount(openingAmount(bill));
        })
        .catch(e => toast.showApiError(e, 'Failed to load supplier bill'))
        .finally(() => setLoadingBill(false));
    } else {
      loadSuppliersSafely(() => SupplierService.listByInstitution(0, 500, 'ALL'))
        .then(r => { setSuppliers(r.suppliers); if (r.error) toast.showError(r.error); })
        .finally(() => setLoadingSuppliers(false));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSupplierChange = async (supplierId: string) => {
    rateTouched.current = false;
    setSelectedSupplierId(supplierId);
    setSelectedBill(null);
    setBills([]);
    setAmount('');
    try {
      const res = await SupplierBillService.listBySupplier(supplierId, 0, 500);
      setBills(res.content.filter(isPayable));
    } catch (e) {
      toast.showApiError(e, 'Failed to load bills for supplier');
    }
  };

  const handleBillChange = (billId: string) => {
    rateTouched.current = false;
    const bill = bills.find(b => b.id === billId) ?? null;
    setSelectedBill(bill);
    if (bill) {
      setAmount(openingAmount(bill));
    } else {
      setAmount('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBill || !paymentAccountId || amount === '' || withholding.pending || (foreign && !(Number(rate) > 0))) return;
    setSaving(true);
    try {
      const payment = await SupplierPaymentService.create({
        supplierBillId: selectedBill.id,
        paymentDate,
        ...(foreign ? { foreignAmount: Number(amount), exchangeRate: Number(rate) } : { amount: Number(amount) }),
        paymentAccountId,
        paymentMethod: paymentMethod || undefined,
        reference: reference || undefined,
        institutionId,
        withholdings: withholding.lines.length > 0 ? withholding.lines : undefined,
      });
      toast.showSuccess('Payment recorded successfully');
      navigate(`/procurement/supplier-payments/${payment.id}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  if (loadingBill || loadingSuppliers) {
    return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 3 }} data-help-page="procurement.paymentForm.page">
      <Typography variant="h5" sx={{ mb: 3 }}>Record Supplier Payment</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 600 }}>
        Once payment approval bands are set, payments cannot be recorded directly here; add the bill to a payment run so someone else approves it first.
      </Typography>
      <Paper sx={{ p: 3, maxWidth: 600 }} data-help-section="procurement.paymentForm.section.main">
        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Bill picker — locked or interactive */}
          {lockedBillId ? (
            <Box>
              <Typography variant="body2" color="text.secondary">Supplier Bill (locked)</Typography>
              <Typography>{selectedBill?.billNumber ?? lockedBillId}</Typography>
            </Box>
          ) : (
            <>
              <SearchableSelect<Supplier>
                options={suppliers}
                value={selectedSupplierId}
                onChange={id => handleSupplierChange(id)}
                getOptionLabel={s => s.name}
                data-help="procurement.paymentForm.field.supplier"
                label="Supplier"
                required
                size="medium"
              />

              {selectedSupplierId && (
                <SearchableSelect<SupplierBill>
                  options={bills}
                  value={selectedBill?.id ?? ''}
                  onChange={id => handleBillChange(id)}
                  getOptionLabel={b => `${b.billNumber} — ${b.status} (Total: ${billTotalLabel(b)}, Outstanding: ${amountLabel(b, bdtOutstanding(b), b.foreignOutstanding)})`}
                  data-help="procurement.paymentForm.field.bill"
                  label="Supplier Bill"
                  required
                  size="medium"
                />
              )}
            </>
          )}

          {/* Bill summary */}
          {selectedBill && (
            <Box sx={{ backgroundColor: 'action.hover', p: 2, borderRadius: 1 }}>
              <Typography variant="body2">Total: {billTotalLabel(selectedBill)}</Typography>
              <Typography variant="body2">Paid: {amountLabel(selectedBill, selectedBill.paidAmount, selectedBill.foreignPaid)}</Typography>
              <Typography variant="body2" fontWeight="bold">Outstanding: {amountLabel(selectedBill, outstanding, selectedBill.foreignOutstanding)}</Typography>
            </Box>
          )}

          <TextField
            data-help="procurement.paymentForm.field.date"
            label="Payment Date"
            type="date"
            value={paymentDate}
            onChange={e => setPaymentDate(e.target.value)}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            data-help="procurement.paymentForm.field.amount"
            label={foreign ? `Amount (${currency})` : 'Amount'}
            type="number"
            value={amount}
            onChange={e => setAmount(e.target.value === '' ? '' : Number(e.target.value))}
            inputProps={{ min: 0, step: 0.01 }}
            fullWidth
            required
          />

          {foreign && (
            <TextField
              data-help="procurement.paymentForm.field.rate"
              label={`Rate (BDT per ${currency})`}
              type="number"
              value={rate}
              onChange={e => { rateTouched.current = true; setRate(e.target.value === '' ? '' : Number(e.target.value)); }}
              inputProps={{ min: 0, step: 'any' }}
              fullWidth
              required
              helperText={
                Number(rate) > 0 && amount !== ''
                  ? `${rateHint ? rateHint + '. ' : ''}= BDT ${formatMoney(Number(amount) * Number(rate))} at the payment rate${assetAccounts.find(a => a.id === paymentAccountId)?.foreignCurrencyCode ? ' (the bank account is credited at its own carrying rate; the difference is realised FX)' : ''}`
                  : rateHint || undefined
              }
            />
          )}

          <PaymentWithholdingSection
            billId={selectedBill?.id ?? null}
            amount={foreign ? (amount === '' || !(Number(rate) > 0) ? '' : Math.round(Number(amount) * Number(rate) * 100) / 100) : amount}
            onChange={setWithholding}
          />

          <AccountAutocomplete
            data-help="procurement.paymentForm.field.account"
            label="Payment Account"
            accounts={assetAccounts}
            value={paymentAccountId}
            onChange={setPaymentAccountId}
            required
            helperText={foreignCurrencyNameHint(assetAccounts.find(a => a.id === paymentAccountId)) ?? undefined}
            error={!!foreignCurrencyNameHint(assetAccounts.find(a => a.id === paymentAccountId))}
          />

          <TextField
            data-help="procurement.paymentForm.field.method"
            label="Payment Method (optional)"
            value={paymentMethod}
            onChange={e => setPaymentMethod(e.target.value)}
            fullWidth
          />

          <TextField
            data-help="procurement.paymentForm.field.reference"
            label="Reference (optional)"
            value={reference}
            onChange={e => setReference(e.target.value)}
            fullWidth
          />

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button data-help="procurement.paymentForm.cancel" variant="outlined" onClick={() => navigate(-1)} disabled={saving}>Cancel</Button>
            <Button
              data-help="procurement.paymentForm.save"
              type="submit"
              variant="contained"
              disabled={saving || withholding.pending || !selectedBill || !paymentAccountId || amount === '' || (foreign && !(Number(rate) > 0))}
            >
              {saving ? 'Saving…' : 'Record Payment'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default SupplierPaymentCreate;
